import React, { useEffect, useState } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ErrorEvent {
  message: string;
  severity?: AlertColor;
  timestamp: number;
  reason?: string;
}

const AuthErrorToast: React.FC = () => {
  const [errors, setErrors] = useState<ErrorEvent[]>([]);

  useEffect(() => {
    const handleAuthError = (event: CustomEvent) => {
      const error: ErrorEvent = {
        message: event.detail.message,
        severity: event.detail.severity || 'error',
        timestamp: Date.now(),
      };
      
      setErrors(prev => [...prev, error]);
    };

    const handleGeneralError = (event: CustomEvent) => {
      const error: ErrorEvent = {
        message: event.detail.message,
        severity: event.detail.severity || 'error',
        timestamp: Date.now(),
      };
      
      setErrors(prev => [...prev, error]);
    };

    const handleAuthTimeout = (event: CustomEvent) => {
      const error: ErrorEvent = {
        message: event.detail.message,
        severity: 'warning',
        timestamp: Date.now(),
        reason: event.detail.reason || 'timeout',
      };
      
      setErrors(prev => [...prev, error]);
    };

    // Listen for auth-specific, general errors, and timeouts
    window.addEventListener('auth-error' as any, handleAuthError);
    window.addEventListener('app-error' as any, handleGeneralError);
    window.addEventListener('auth-timeout' as any, handleAuthTimeout);
    
    return () => {
      window.removeEventListener('auth-error' as any, handleAuthError);
      window.removeEventListener('app-error' as any, handleGeneralError);
      window.removeEventListener('auth-timeout' as any, handleAuthTimeout);
    };
  }, []);

  const handleClose = (timestamp: number) => {
    setErrors(prev => prev.filter(e => e.timestamp !== timestamp));
  };

  // Display the most recent error
  const currentError = errors[errors.length - 1];

  return currentError ? (
    <Snackbar 
      open={true} 
      autoHideDuration={currentError.reason === 'inactivity' ? 8000 : 6000} 
      onClose={() => handleClose(currentError.timestamp)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={() => handleClose(currentError.timestamp)} 
        severity={currentError.severity} 
        sx={{ 
          width: '100%',
          maxWidth: 500,
          '& .MuiAlert-message': {
            fontSize: '0.95rem',
          },
        }}
      >
        {currentError.message}
      </Alert>
    </Snackbar>
  ) : null;
};

export default AuthErrorToast;